import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { forumApi } from '../../api/forum';
import { filesApi } from '../../api/files';
import { UserContext } from '../../context/UserContext';
import HrnbButton from '../controls/HrnbButton';
import HrnbForm from '../controls/HrnbForm';
import HrnbInput from '../controls/HrnbInput';
import HrnbSelect from '../controls/HrnbSelect';
import HrnbTextArea from '../controls/HrnbTextArea';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import FileUploader from './FileUploader';
import '../../css/components/CreatePostForm.css'
import HrnbModal from "../controls/HrnbModal";

export default function CreatePostForm({ isOpen, onClose, onSuccess, categories = [] }) {
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        categoryId: '',
        tags: '',
    });
    const [authorProfileId, setAuthorProfileId] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    useEffect(() => {
        if (!currentUser) return;
        const pid = currentUser.profiles?.[0]?.id;
        if (pid) {
            setAuthorProfileId(pid);
        }
    }, [currentUser]);

    // Если категорий нет, используем заглушки
    const displayedCategories = categories.length > 0 ? categories : [
        { id: 1, name: 'Обсуждения' },
        { id: 2, name: 'Вопросы и ответы' },
        { id: 3, name: 'Новости' },
        { id: 4, name: 'Предложения' }
    ];

    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const validateForm = () => {
        if (!formData.title || !formData.title.trim()) {
            setError('Название поста обязательно');
            return false;
        }
        if (formData.title.trim().length < 3) {
            setError('Название поста должно быть не менее 3 символов');
            return false;
        }
        if (!formData.content || !formData.content.trim()) {
            setError('Содержимое поста обязательно');
            return false;
        }
        if (formData.content.trim().length < 10) {
            setError('Содержимое поста должно быть не менее 10 символов');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!currentUser) {
            setError('Необходимо авторизоваться для создания поста');
            return;
        }
        if (!authorProfileId) {
            setError('Не удалось определить профиль автора. Попробуйте позже.');
            return;
        }

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            const attachmentIds = uploadedFiles.map(f => f.id);

            const payload = {
                title: formData.title.trim(),
                content: formData.content.trim(),
                categoryId: formData.categoryId,
                authorId: authorProfileId,
                tags: formData.tags
                    ? formData.tags.split(',').map(t => t.trim()).filter(t => t)
                    : [],
                attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
            };

            const result = await forumApi.createPost(payload);
            setSuccess(true);

            setTimeout(() => {
                const responseData = result.data || result;
                const postId = responseData?.id || responseData?.postId || result.id || result.postId;

                if (onSuccess) {
                    onSuccess(responseData);
                }
                handleClose();
                if (postId) {
                    navigate(`/forum/posts/${postId}`);
                }
            }, 1000);
        } catch (err) {
            console.error('[CreatePostForm] Failed to create post:', err);

            const errorData = err.response?.data;
            const errorMessage = errorData?.message ||
                                errorData?.error ||
                                err.response?.statusText ||
                                err.message ||
                                'Не удалось создать пост. Попробуйте позже.';

            if (err.response?.status === 500) {
                setError('Ошибка сервера. Обратитесь к администратору или попробуйте позже.');
            } else {
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            title: '',
            content: '',
            categoryId: '',
            tags: '',
        });
        setUploadedFiles([]);
        setError(null);
        setSuccess(false);
        if (onClose) {
            onClose();
        }
    };

    const handleFileUploaded = (fileData) => {
        setUploadedFiles(prev => [...prev, fileData]);
    };

    const handleFileRemoved = (fileId) => {
        setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    };

    if (!isOpen) return null;

    return (
        <HrnbModal open={isOpen} onClose={handleClose} title="Создание нового поста" size="large">
            <div className="create-post-form">
                {error && (
                    <HrnbAlert variant="danger" onClose={() => setError(null)}>
                        {error}
                    </HrnbAlert>
                )}

                {success && (
                    <HrnbAlert variant="success" onClose={() => setSuccess(false)}>
                        Пост успешно создан!
                    </HrnbAlert>
                )}

                <HrnbForm onSubmit={handleSubmit}>
                    <div className="form-row">
                        <div className="form-control-wrapper">
                            <label className="form-control-label">Название *</label>
                            <HrnbInput
                                type="text"
                                value={formData.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                placeholder="Введите название поста"
                                disabled={loading}
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-control-wrapper">
                            <label className="form-control-label">Категория *</label>
                            <HrnbSelect
                                value={formData.categoryId}
                                onChange={(value) => handleChange('categoryId', value)}
                                options={displayedCategories.map(cat => ({
                                    value: cat.id.toString(),
                                    label: cat.name
                                }))}
                                placeholder="Выберите категорию"
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-control-wrapper">
                            <label className="form-control-label">Содержимое *</label>
                            <HrnbTextArea
                                value={formData.content}
                                onChange={(e) => handleChange('content', e.target.value)}
                                placeholder="Напишите содержимое поста..."
                                rows={8}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-control-wrapper">
                            <label className="form-control-label">Теги</label>
                            <HrnbInput
                                type="text"
                                value={formData.tags}
                                onChange={(e) => handleChange('tags', e.target.value)}
                                placeholder="Теги через запятую (например: javascript, react, tutorial)"
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-control-wrapper">
                            <label className="form-control-label">Вложения (изображения, аудио, видео, документы)</label>
                            <FileUploader
                                onFileUploaded={handleFileUploaded}
                                onFileRemoved={handleFileRemoved}
                                uploadedFiles={uploadedFiles}
                                metadata="forum_attachment"
                                maxFileSize={30}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="form-actions">
                        <HrnbButton variant="outline" onClick={handleClose} disabled={loading}>
                            Отмена
                        </HrnbButton>
                        <HrnbButton type="submit" variant="primary" disabled={loading}>
                            {loading ? <HrnbLoader size="sm" /> : 'Создать пост'}
                        </HrnbButton>
                    </div>
                </HrnbForm>
            </div>
        </HrnbModal>
    );
}
